import React, { useEffect, useState } from 'react';import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LogOut, ArrowLeft, Sparkles, Award, IndianRupee, Calendar, Gift, Users, TrendingUp, Plus, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const AdminReports = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('founding');
  const [founding, setFounding] = useState({ totals: {}, founding_members: [] });
  const [subsReport, setSubsReport] = useState({ totals: {}, subscriptions: [] });
  const [partnersReport, setPartnersReport] = useState({ rows: [], totals: {} });
  const [revenueBreakdown, setRevenueBreakdown] = useState({ rows: [] });
  const [dsaLeads, setDsaLeads] = useState({ items: [], total: 0 });
  const [partners, setPartners] = useState([]);
  const [expiry, setExpiry] = useState({ subscriptions: [], count: 0 });
  const [complimentary, setComplimentary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Monthly GST Report state (Feb 2026 — GSTR-1 ready)
  const now = new Date();
  const [gstYear, setGstYear] = useState(now.getFullYear());
  const [gstMonth, setGstMonth] = useState(now.getMonth() + 1);
  const [gstType, setGstType] = useState('paid'); // 'paid' | 'registrations'
  const [gstHomeState, setGstHomeState] = useState(() => {
    try { return localStorage.getItem('gst_home_state') || 'Maharashtra'; } catch { return 'Maharashtra'; }
  });
  const [gst, setGst] = useState({ rows: [], totals: {}, metadata: {} });
  const [gstLoading, setGstLoading] = useState(false);
  
  const [grantModal, setGrantModal] = useState(null); // {user_id, name, email}
  const [grantForm, setGrantForm] = useState({ plan_key: 'buyer_yearly', is_complimentary: false, discount_percent: 0, duration_days: 365, notes: '', mark_as_founding: false });
  // Founder mandate (Feb 2026 P0 Isolation): reports default to real production data only.
  // Super-Admin can toggle to see raw counts including demo/QA/test residue.
  const [includeTest, setIncludeTest] = useState(() => {
    try { return localStorage.getItem('admin_include_test') === '1'; } catch { return false; }
  });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => { fetchAll(); }, [includeTest]);

  const fetchAll = async () => {
    setLoading(true);
    const qs = includeTest ? '?include_test=1' : '';
    const qsExpiry = includeTest ? '&include_test=1' : '';
    try {
      const [f, s, p, pl, e, c, br, leads] = await Promise.all([
        axios.get(`${API}/admin/memberships/report/founding${qs}`, { withCredentials: true }),
        axios.get(`${API}/admin/memberships/report/subscriptions${qs}`, { withCredentials: true }),
        axios.get(`${API}/partners/admin/report/revenue`, { withCredentials: true }),
        axios.get(`${API}/partners/admin/all${qs}`, { withCredentials: true }),
        axios.get(`${API}/admin/memberships/report/expiry?days=30${qsExpiry}`, { withCredentials: true }),
        axios.get(`${API}/admin/memberships/report/complimentary${qs}`, { withCredentials: true }),
        axios.get(`${API}/partners/admin/report/revenue/breakdown`, { withCredentials: true }),
        axios.get(`${API}/partners/admin/dsa-leads`, { withCredentials: true }),
      ]);
      setFounding(f.data); setSubsReport(s.data); setPartnersReport(p.data); setPartners(pl.data); setExpiry(e.data); setComplimentary(Array.isArray(c.data) ? c.data : (c.data?.subscriptions || []));
      setRevenueBreakdown(br.data); setDsaLeads(leads.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const onToggleIncludeTest = (e) => {
    const on = e.target.checked;
    setIncludeTest(on);
    try { localStorage.setItem('admin_include_test', on ? '1' : '0'); } catch (_) { /* localStorage may be disabled */ }
  };

  // ─── Monthly GST Report ─────────────────────────────────────
  const fetchGst = async () => {
    setGstLoading(true);
    try {
      const params = new URLSearchParams({
        year: String(gstYear), month: String(gstMonth), type: gstType, home_state: gstHomeState,
      });
      if (includeTest) params.set('include_test', '1');
      const res = await axios.get(`${API}/admin/memberships/report/gst-monthly?${params}`, { withCredentials: true });
      setGst(res.data);
    } catch (err) {
      toast.error('Failed to load GST report');
    } finally {
      setGstLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'gst-monthly') fetchGst();
  }, [activeTab, gstYear, gstMonth, gstType, gstHomeState, includeTest]);

  const downloadGst = async (fmtKind /* 'csv' | 'pdf' */) => {
    try {
      const params = new URLSearchParams({
        year: String(gstYear), month: String(gstMonth), type: gstType, home_state: gstHomeState,
      });
      if (includeTest) params.set('include_test', '1');
      const url = `${API}/admin/memberships/report/gst-monthly.${fmtKind}?${params}`;
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmtKind === 'csv' ? 'text/csv' : 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `gst_monthly_${gstType}_${gstYear}-${String(gstMonth).padStart(2, '0')}.${fmtKind}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${fmtKind.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(`Failed to download ${fmtKind.toUpperCase()}`);
    }
  };

  const onGstHomeStateChange = (v) => {
    setGstHomeState(v);
    try { localStorage.setItem('gst_home_state', v); } catch (_) { /* ignore */ }
  };

  const handleApprovePartner = async (userId, approved) => {
    try {
      await axios.patch(`${API}/admin/users/approve`, { user_id: userId, approved }, { withCredentials: true });
      toast.success(`Partner ${approved ? 'approved' : 'rejected'}`);
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/memberships/grant`, { user_id: grantModal.user_id, ...grantForm }, { withCredentials: true });
      toast.success('Membership granted');
      setGrantModal(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMarkFounding = async (userId) => {
    if (!window.confirm('Mark this user as Founding Member?')) return;
    try {
      await axios.post(`${API}/admin/memberships/mark-founding/${userId}`, {}, { withCredentials: true });
      toast.success('Marked as Founding Member');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const handleExtend = async (subId) => {
    const days = window.prompt('Extend by how many days?', '30');
    if (!days) return;
    try {
      await axios.post(`${API}/admin/memberships/extend`, { subscription_id: subId, extend_days: parseInt(days) }, { withCredentials: true });
      toast.success('Extended successfully');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const handleRenew = async (subId) => {
    if (!window.confirm('Create a renewal subscription?')) return;
    try {
      await axios.post(`${API}/admin/memberships/renew/${subId}`, {}, { withCredentials: true });
      toast.success('Renewal subscription created');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate('/admin-dashboard')} className="text-white/70 hover:text-white" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
            <Sparkles className="h-6 w-6 text-gold" />
            <h1 className="text-xl font-bold">Reports & Memberships</h1>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-white/70">{user?.email}</span>
            <button onClick={handleLogout} className="px-3 py-1.5 rounded-md border border-white/30 text-sm" data-testid="logout-btn"><LogOut className="inline h-4 w-4 mr-1" /> Logout</button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Founder mandate (Feb 2026 P0 Isolation): production-only data by default */}
        <div className="flex justify-end mb-4">
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" data-testid="reports-include-test-label">
            <input
              type="checkbox"
              checked={includeTest}
              onChange={onToggleIncludeTest}
              data-testid="reports-include-test-toggle"
            />
            <span>Include test / demo / rejected records</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">(Super-Admin only — production data hidden by default)</span>
          </label>
        </div>
        {includeTest && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="reports-include-test-banner">
            ⚠ You are viewing <strong>raw counts</strong> including test / demo / rejected accounts. Toggle off to return to production-only view.
          </div>
        )}
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
          {[
            { id: 'founding', label: 'Founding Members', icon: Award },
            { id: 'subscriptions', label: 'Subscriptions & Revenue', icon: IndianRupee },
            { id: 'partners', label: 'Partners & DSA', icon: Users },
            { id: 'breakdown', label: 'First-time vs Renewal', icon: TrendingUp },
            { id: 'dsa-leads', label: 'DSA Leads', icon: Plus },
            { id: 'feedback', label: 'Feedback', icon: Award },
            { id: 'expiry', label: 'Expiry (30d)', icon: Calendar },
            { id: 'complimentary', label: 'Complimentary/Discounted', icon: Gift },
            { id: 'gst-monthly', label: 'Monthly GST Report', icon: FileText },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 font-medium text-sm whitespace-nowrap inline-flex items-center ${activeTab === t.id ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid={`tab-${t.id}`}>
                <Icon className="h-4 w-4 mr-2" />{t.label}
              </button>
            );
          })}
        </div>

        {loading ? <div className="text-center py-12">Loading reports...</div> : (
          <>
            {/* Founding Members */}
            {activeTab === 'founding' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="p-5 rounded-lg bg-navy text-white">
                    <div className="text-xs uppercase tracking-wider text-gold mb-2">Founding Buyers</div>
                    <div className="text-3xl font-bold" data-testid="founding-buyer-count">{founding.totals?.founding_buyers || 0} / 100</div>
                    <div className="text-xs text-white/70 mt-1">{founding.totals?.buyer_remaining || 0} slots remaining</div>
                  </div>
                  <div className="p-5 rounded-lg bg-navy text-white">
                    <div className="text-xs uppercase tracking-wider text-orange mb-2">Founding Suppliers</div>
                    <div className="text-3xl font-bold" data-testid="founding-supplier-count">{founding.totals?.founding_suppliers || 0} / 100</div>
                    <div className="text-xs text-white/70 mt-1">{founding.totals?.supplier_remaining || 0} slots remaining</div>
                  </div>
                  <div className="p-5 rounded-lg border border-border bg-card">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Total Founding</div>
                    <div className="text-3xl font-bold text-navy">{founding.totals?.total_founding || 0}</div>
                  </div>
                  <div className="p-5 rounded-lg border border-gold bg-gold/5">
                    <div className="text-xs uppercase tracking-wider text-navy mb-2">Subscription Revenue</div>
                    <div className="text-2xl font-bold text-navy">{fmt(subsReport.totals?.revenue_paid)}</div>
                    <div className="text-xs text-muted-foreground mt-1">From {subsReport.totals?.count_paid || 0} paid subs</div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-navy mb-4">Founding Members List</h2>
                  {founding.founding_members?.length === 0 ? <p className="text-muted-foreground py-8 text-center">No founding members yet.</p> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b"><th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Email</th><th className="text-left py-2 px-3">Role</th><th className="text-left py-2 px-3">Marked At</th></tr></thead>
                        <tbody>
                          {founding.founding_members.map(m => (
                            <tr key={m.id} className="border-b" data-testid={`founding-row-${m.id}`}>
                              <td className="py-2 px-3">{m.name}</td>
                              <td className="py-2 px-3">{m.email}</td>
                              <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs bg-gold/20 text-navy">{m.role}</span></td>
                              <td className="py-2 px-3 text-xs">{m.marked_founding_at ? new Date(m.marked_founding_at).toLocaleString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Subscriptions */}
            {activeTab === 'subscriptions' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Revenue Paid</div><div className="text-xl font-bold text-navy">{fmt(subsReport.totals?.revenue_paid)}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Paid Subs</div><div className="text-xl font-bold">{subsReport.totals?.count_paid || 0}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Pending</div><div className="text-xl font-bold">{subsReport.totals?.count_pending || 0}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Complimentary</div><div className="text-xl font-bold">{subsReport.totals?.count_complimentary || 0}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Discounted</div><div className="text-xl font-bold">{subsReport.totals?.count_discounted || 0}</div></div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-navy">All Subscriptions</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2 px-3">User</th><th className="text-left py-2 px-3">Plan</th>
                        <th className="text-right py-2 px-3">Amount</th><th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Expires</th><th className="text-left py-2 px-3">Actions</th>
                      </tr></thead>
                      <tbody>
                        {subsReport.subscriptions?.map(s => (
                          <tr key={s.id} className="border-b" data-testid={`sub-row-${s.id}`}>
                            <td className="py-2 px-3"><div>{s.user_name}</div><div className="text-xs text-muted-foreground">{s.user_email}</div></td>
                            <td className="py-2 px-3">{s.plan_name}</td>
                            <td className="py-2 px-3 text-right">{fmt(s.total_amount)}</td>
                            <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs bg-navy/10 text-navy">{s.payment_status}</span></td>
                            <td className="py-2 px-3 text-xs">{s.end_date ? new Date(s.end_date).toLocaleDateString() : '—'}</td>
                            <td className="py-2 px-3 space-x-2">
                              <button onClick={() => handleExtend(s.id)} className="text-navy hover:underline text-xs" data-testid={`extend-${s.id}`}>Extend</button>
                              <button onClick={() => handleRenew(s.id)} className="text-green-600 hover:underline text-xs" data-testid={`renew-${s.id}`}>Renew</button>
                              <a href={`${API}/payments/invoice/${s.id}`} target="_blank" rel="noopener noreferrer" className="text-navy hover:underline text-xs" data-testid={`invoice-${s.id}`}>Invoice PDF</a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Partners */}
            {activeTab === 'partners' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Total Partners</div><div className="text-2xl font-bold text-navy">{partnersReport.totals?.partners || 0}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Revenue Attributed</div><div className="text-xl font-bold">{fmt(partnersReport.totals?.total_revenue_attributed)}</div></div>
                  <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">Commission Earned</div><div className="text-xl font-bold">{fmt(partnersReport.totals?.total_commission_earned)}</div></div>
                  <div className="p-4 rounded-lg border-2 border-gold bg-gold/5"><div className="text-xs text-muted-foreground">Outstanding Payouts</div><div className="text-xl font-bold text-navy">{fmt(partnersReport.totals?.total_outstanding)}</div></div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-navy mb-4">Partner & DSA Registrations</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Type</th>
                        <th className="text-left py-2 px-3">Code</th><th className="text-right py-2 px-3">Refs</th>
                        <th className="text-right py-2 px-3">Conv</th><th className="text-right py-2 px-3">Revenue</th>
                        <th className="text-right py-2 px-3">Commission</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Action</th>
                      </tr></thead>
                      <tbody>
                        {partners.map(p => (
                          <tr key={p.id} className="border-b" data-testid={`partner-row-${p.id}`}>
                            <td className="py-2 px-3"><div>{p.name}</div><div className="text-xs text-muted-foreground">{p.email}</div></td>
                            <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded text-xs ${p.partner_type === 'dsa' ? 'bg-orange/20 text-orange' : 'bg-navy/10 text-navy'}`}>{p.partner_type === 'dsa' ? 'DSA' : 'Referral'}</span></td>
                            <td className="py-2 px-3 font-mono text-xs">{p.referral_code}</td>
                            <td className="py-2 px-3 text-right">{p.total_referrals || 0}</td>
                            <td className="py-2 px-3 text-right">{p.total_conversions || 0}</td>
                            <td className="py-2 px-3 text-right">{fmt(p.total_revenue_attributed)}</td>
                            <td className="py-2 px-3 text-right">{fmt(p.total_commission_earned)}</td>
                            <td className="py-2 px-3">{p.approved ? <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Active</span> : <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">Pending</span>}</td>
                            <td className="py-2 px-3">
                              {!p.approved ? (
                                <button onClick={() => handleApprovePartner(p.user_id, true)} className="text-green-600 hover:underline text-xs" data-testid={`approve-partner-${p.id}`}>Approve</button>
                              ) : <span className="text-xs text-muted-foreground">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* First-time vs Renewal Breakdown */}
            {activeTab === 'breakdown' && (
              <div className="bg-card border border-border rounded-lg p-6" data-testid="renewal-breakdown-section">
                <h2 className="text-lg font-semibold text-navy mb-4">Commission Revenue — First-time vs Renewal</h2>
                {revenueBreakdown.rows?.length === 0 ? <p className="text-center py-8 text-muted-foreground">No conversion data yet.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2 px-3">Partner</th>
                        <th className="text-left py-2 px-3">Type</th>
                        <th className="text-right py-2 px-3">1st Count</th>
                        <th className="text-right py-2 px-3">1st Revenue</th>
                        <th className="text-right py-2 px-3">1st Commission</th>
                        <th className="text-right py-2 px-3">Renewal Count</th>
                        <th className="text-right py-2 px-3">Renewal Revenue</th>
                        <th className="text-right py-2 px-3">Renewal Commission</th>
                      </tr></thead>
                      <tbody>
                        {revenueBreakdown.rows.map(r => (
                          <tr key={r.partner_id} className="border-b" data-testid={`breakdown-row-${r.partner_id}`}>
                            <td className="py-2 px-3 font-mono text-xs">{r.referral_code}</td>
                            <td className="py-2 px-3 capitalize">{r.partner_type === 'dsa' ? 'DSA' : 'RP'}</td>
                            <td className="py-2 px-3 text-right">{r.first_time_count}</td>
                            <td className="py-2 px-3 text-right">₹{(r.first_time_revenue || 0).toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right text-green-700">₹{(r.first_time_commission || 0).toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right">{r.renewal_count}</td>
                            <td className="py-2 px-3 text-right">₹{(r.renewal_revenue || 0).toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right text-orange">₹{(r.renewal_commission || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* DSA Leads */}
            {activeTab === 'dsa-leads' && (
              <div className="bg-card border border-border rounded-lg p-6" data-testid="admin-dsa-leads-section">
                <h2 className="text-lg font-semibold text-navy mb-4">All DSA Leads ({dsaLeads.total})</h2>
                {dsaLeads.items?.length === 0 ? <p className="text-center py-8 text-muted-foreground">No DSA leads logged yet.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2 px-3">DSA</th>
                        <th className="text-left py-2 px-3">Contact</th>
                        <th className="text-left py-2 px-3">Org / Location</th>
                        <th className="text-left py-2 px-3">Intended</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Date</th>
                      </tr></thead>
                      <tbody>
                        {dsaLeads.items.map(l => (
                          <tr key={l.id} className="border-b" data-testid={`admin-lead-${l.id}`}>
                            <td className="py-2 px-3">
                              <div className="text-xs">{l.dsa_name}</div>
                              <div className="text-xs text-muted-foreground font-mono">{l.referral_code}</div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-medium">{l.contact_name}</div>
                              <div className="text-xs text-muted-foreground">{l.contact_email} {l.contact_mobile && `• ${l.contact_mobile}`}</div>
                            </td>
                            <td className="py-2 px-3 text-xs">{l.organization || '—'}<br/>{l.city}{l.state ? `, ${l.state}` : ''}</td>
                            <td className="py-2 px-3 capitalize text-xs">{l.intended_role || '—'}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${l.status === 'converted' ? 'bg-green-100 text-green-800' : l.status === 'qualified' ? 'bg-navy/10 text-navy' : l.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' : l.status === 'dropped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{l.status}</span>
                            </td>
                            <td className="py-2 px-3 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Feedback */}
            {activeTab === 'feedback' && <FeedbackTab API={API} />}

            {/* Expiry */}
            {activeTab === 'expiry' && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-navy mb-4">Memberships Expiring in next 30 days ({expiry.count})</h2>
                {expiry.subscriptions?.length === 0 ? <p className="text-center py-8 text-muted-foreground">No memberships expiring soon.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-2 px-3">User</th><th className="text-left py-2 px-3">Plan</th><th className="text-left py-2 px-3">Expires</th><th className="text-right py-2 px-3">Days Left</th><th className="text-left py-2 px-3">Actions</th></tr></thead>
                    <tbody>
                      {expiry.subscriptions.map(s => (
                        <tr key={s.id} className="border-b" data-testid={`expiry-row-${s.id}`}>
                          <td className="py-2 px-3"><div>{s.user_name}</div><div className="text-xs text-muted-foreground">{s.user_email}</div></td>
                          <td className="py-2 px-3">{s.plan_name}</td>
                          <td className="py-2 px-3 text-xs">{s.end_date ? new Date(s.end_date).toLocaleDateString() : '—'}</td>
                          <td className="py-2 px-3 text-right"><span className={`font-medium ${s.days_until_expiry < 7 ? 'text-red-600' : s.days_until_expiry < 14 ? 'text-orange-600' : ''}`}>{s.days_until_expiry}d</span></td>
                          <td className="py-2 px-3 space-x-2">
                            <button onClick={() => handleExtend(s.id)} className="text-navy hover:underline text-xs">Extend</button>
                            <button onClick={() => handleRenew(s.id)} className="text-green-600 hover:underline text-xs">Renew</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Complimentary */}
            {activeTab === 'complimentary' && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-navy mb-4">Complimentary & Discounted Memberships ({complimentary.length})</h2>
                {complimentary.length === 0 ? <p className="text-center py-8 text-muted-foreground">No complimentary/discounted memberships yet.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-2 px-3">User</th><th className="text-left py-2 px-3">Plan</th><th className="text-left py-2 px-3">Type</th><th className="text-right py-2 px-3">Discount</th><th className="text-left py-2 px-3">Notes</th><th className="text-left py-2 px-3">Granted</th></tr></thead>
                    <tbody>
                      {complimentary.map(s => (
                        <tr key={s.id} className="border-b" data-testid={`comp-row-${s.id}`}>
                          <td className="py-2 px-3"><div>{s.user_name}</div><div className="text-xs text-muted-foreground">{s.user_email}</div></td>
                          <td className="py-2 px-3">{s.plan_name}</td>
                          <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded text-xs ${s.is_complimentary ? 'bg-green-100 text-green-800' : 'bg-navy/10 text-navy'}`}>{s.is_complimentary ? 'COMP' : 'DISCOUNT'}</span></td>
                          <td className="py-2 px-3 text-right">{s.discount_percent || 0}%</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">{s.notes || '—'}</td>
                          <td className="py-2 px-3 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Monthly GST Report (GSTR-1 ready) */}
            {activeTab === 'gst-monthly' && (
              <div className="space-y-4" data-testid="gst-monthly-tab">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="grid md:grid-cols-5 gap-3 items-end">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Year</label>
                      <select value={gstYear} onChange={e => setGstYear(parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="gst-year">
                        {Array.from({ length: 6 }).map((_, i) => {
                          const y = new Date().getFullYear() - i;
                          return <option key={y} value={y}>{y}</option>;
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Month</label>
                      <select value={gstMonth} onChange={e => setGstMonth(parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="gst-month">
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <option key={i} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Report Type</label>
                      <select value={gstType} onChange={e => setGstType(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="gst-type">
                        <option value="paid">Paid Subscriptions</option>
                        <option value="registrations">All Registrations</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Home State</label>
                      <select value={gstHomeState} onChange={e => onGstHomeStateChange(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="gst-home-state">
                        {['Maharashtra','Karnataka','Tamil Nadu','Delhi','Gujarat','Uttar Pradesh','West Bengal','Telangana','Andhra Pradesh','Kerala','Rajasthan','Haryana','Madhya Pradesh','Punjab','Odisha','Bihar','Jharkhand','Chhattisgarh','Assam','Goa','Uttarakhand','Himachal Pradesh','Jammu and Kashmir','Chandigarh'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => downloadGst('csv')} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-navy text-white text-sm font-semibold hover:opacity-90" data-testid="gst-download-csv">
                        <Download className="h-4 w-4" /> CSV
                      </button>
                      <button onClick={() => downloadGst('pdf')} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-orange text-white text-sm font-semibold hover:opacity-90" data-testid="gst-download-pdf">
                        <Download className="h-4 w-4" /> PDF
                      </button>
                    </div>
                  </div>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {[
                    { label: 'Rows', value: gst.totals?.row_count || 0, prefix: '' },
                    { label: 'Basic Amount', value: gst.totals?.basic_amount || 0, prefix: '₹' },
                    { label: 'CGST', value: gst.totals?.cgst_amount || 0, prefix: '₹' },
                    { label: 'SGST', value: gst.totals?.sgst_amount || 0, prefix: '₹' },
                    { label: 'IGST', value: gst.totals?.igst_amount || 0, prefix: '₹' },
                    { label: 'Grand Total', value: gst.totals?.total_amount || 0, prefix: '₹', highlight: true },
                  ].map(t => (
                    <div key={t.label} className={`p-3 rounded-lg border ${t.highlight ? 'bg-navy text-white border-navy' : 'bg-card border-border'}`}>
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.highlight ? 'text-gold' : 'text-muted-foreground'}`}>{t.label}</div>
                      <div className="text-lg font-bold">{t.prefix}{t.prefix ? Number(t.value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : t.value}</div>
                    </div>
                  ))}
                </div>

                {/* Rows table */}
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  {gstLoading ? (
                    <div className="text-center py-12 text-muted-foreground">Loading GST report…</div>
                  ) : (gst.rows || []).length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground" data-testid="gst-empty">
                      No GST-liable transactions in {['','January','February','March','April','May','June','July','August','September','October','November','December'][gstMonth]} {gstYear}.
                      <div className="text-xs mt-2">Real customer subscriptions will populate this report after payment. On preview, UAT accounts using RFC-2606 reserved emails are hidden by default — toggle &quot;Include test / demo / rejected records&quot; above to see them.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Party</th>
                            <th className="px-3 py-2 text-left">PAN</th>
                            <th className="px-3 py-2 text-left">GSTIN</th>
                            <th className="px-3 py-2 text-left">Place of Supply</th>
                            <th className="px-3 py-2 text-right">Basic</th>
                            <th className="px-3 py-2 text-right">CGST</th>
                            <th className="px-3 py-2 text-right">SGST</th>
                            <th className="px-3 py-2 text-right">IGST</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(gst.rows || []).map((r, idx) => (
                            <tr key={r.invoice_receipt_id || idx} className="border-t border-border" data-testid={`gst-row-${idx}`}>
                              <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                              <td className="px-3 py-2">
                                <div className="font-medium">{r.party_name}</div>
                                <div className="text-xs text-muted-foreground">{r.party_email} · <span className="uppercase">{r.role}</span></div>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{r.pan}</td>
                              <td className="px-3 py-2 font-mono text-xs">{r.gstin}</td>
                              <td className="px-3 py-2">
                                {r.place_of_supply}
                                <div className="text-[10px] text-muted-foreground">{r.supply_type}</div>
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(r.basic_amount)}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(r.cgst_amount)}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(r.sgst_amount)}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(r.igst_amount)}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{fmt(r.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Intra-state = CGST + SGST (each half of the 18% GST). Inter-state = IGST (full 18%). Home state is used to bucket the split — change it above if your registered office is elsewhere.
                </div>
              </div>
            )}
          </>
        )}

        {/* Quick Grant CTA (always visible) */}
        <div className="mt-8 p-4 rounded-lg border border-gold bg-gold/5 flex items-center justify-between">
          <div>
            <div className="font-semibold text-navy">Manual Membership Actions</div>
            <div className="text-sm text-muted-foreground">Grant, extend, renew, or mark users as Founding Members from the Users tab in the main Admin Dashboard.</div>
          </div>
          <button onClick={() => navigate('/admin-dashboard')} className="px-4 py-2 rounded-md bg-navy text-white font-semibold text-sm" data-testid="open-users-btn">
            Open Users
          </button>
        </div>
      </div>
    </div>
  );
};

const FeedbackTab = ({ API }) => {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?feedback_type=${filter}` : '';
      const { data } = await axios.get(`${API}/admin/feedback${params}`, { withCredentials: true });
      setItems(data || []);
    } catch (err) {
      toast.error('Failed to load feedback');
    } finally { setLoading(false); }
  }, [API, filter]);
  React.useEffect(() => { load(); }, [load]);

  const toggleHide = async (id) => {
    try { await axios.patch(`${API}/admin/feedback/${id}`, {}, { withCredentials: true }); toast.success('Updated'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="admin-feedback-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-navy">Feedback ({items.length})</h2>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="text-sm px-3 py-1 border rounded" data-testid="feedback-type-filter">
          <option value="">All</option>
          <option value="general">General</option>
          <option value="award">Post-Award</option>
        </select>
      </div>
      {loading ? <div className="text-center py-8">Loading...</div> : items.length === 0 ? <p className="text-center py-8 text-muted-foreground">No feedback yet.</p> : (
        <div className="space-y-3">
          {items.map(f => (
            <div key={f.id} className={`p-3 rounded border ${f.hidden ? 'opacity-40 bg-muted/30' : 'border-border'}`} data-testid={`feedback-row-${f.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-orange">{'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)}</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-muted">{f.type}</span>
                    {f.direction && <span className="text-xs text-muted-foreground">{f.direction}</span>}
                  </div>
                  {f.comment && <p className="text-sm mb-1">{f.comment}</p>}
                  <div className="text-xs text-muted-foreground">
                    {f.name && <span>{f.name} </span>}
                    {f.email && <span>· {f.email} </span>}
                    {f.page && <span>· {f.page} </span>}
                    · {new Date(f.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => toggleHide(f.id)} className="text-xs text-navy hover:underline" data-testid={`feedback-hide-${f.id}`}>
                  {f.hidden ? 'Unhide' : 'Hide'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReports;
